import { useState } from 'react';

interface GameProps {
  onComplete: () => void;
}

export default function Game({ onComplete }: GameProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [completed, setCompleted] = useState(false);

  const questions = [
    {
      question: 'Как часто ты посещаешь магазины Золотого Яблока?',
      options: [
        'Несколько раз в неделю',
        'Раз в неделю',
        'Несколько раз в месяц',
        'Раз в месяц или реже'
      ]
    },
    {
      question: 'Что тебе больше всего нравится в Золотом Яблоке?',
      options: [
        'Широкий ассортимент продукции',
        'Качество обслуживания',
        'Программа лояльности',
        'Удобное расположение магазинов'
      ]
    },
    {
      question: 'Какие категории товаров ты покупаешь чаще всего?',
      options: [
        'Уход за кожей',
        'Декоративная косметика',
        'Парфюмерия',
        'Уход за волосами'
      ]
    },
    {
      question: 'Как ты оцениваешь работу консультантов?',
      options: [
        'Отлично - всегда помогают',
        'Хорошо - в основном довольна',
        'Удовлетворительно',
        'Требует улучшения'
      ]
    },
    {
      question: 'Порекомендуешь ли ты Золотое Яблоко друзьям?',
      options: [
        'Определённо да',
        'Скорее да',
        'Скорее нет',
        'Определённо нет'
      ]
    }
  ];

  const handleAnswer = (answer: string) => {
    const newAnswers = { ...answers, [currentQuestion]: answer };
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
      }, 300);
    } else {
      setTimeout(() => {
        setCompleted(true);
        onComplete();
      }, 300);
    }
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <section id="game" className="py-24 px-4 bg-white">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Опрос о Золотом Яблоке
          </h2>
          <p className="text-lg text-gray-600">
            {completed ? 'Спасибо за участие!' : `Вопрос ${currentQuestion + 1} из ${questions.length}`}
          </p>
        </div>

        {!completed ? (
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-8 md:p-12 shadow-xl">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-amber-500 to-orange-500 h-full transition-all duration-500 rounded-full"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 mt-2 text-center">{Math.round(progress)}% завершено</p>
            </div>

            {/* Question */}
            <div className="mb-8">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-8 text-center">
                {questions[currentQuestion].question}
              </h3>

              <div className="space-y-4">
                {questions[currentQuestion].options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswer(option)}
                    className="w-full bg-white hover:bg-gradient-to-r hover:from-amber-500 hover:to-orange-500 text-gray-800 hover:text-white p-6 rounded-2xl text-left transition-all duration-300 shadow-md hover:shadow-xl hover:scale-105 cursor-pointer group"
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-orange-100 group-hover:bg-white/20 flex items-center justify-center mr-4 flex-shrink-0">
                        <span className="font-bold text-orange-600 group-hover:text-white">
                          {String.fromCharCode(65 + index)}
                        </span>
                      </div>
                      <span className="text-lg font-medium">{option}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-3xl p-12 text-center shadow-2xl">
            <div className="mb-6">
              <i className="ri-checkbox-circle-fill text-8xl text-white animate-bounce"></i>
            </div>
            <h3 className="text-4xl font-bold text-white mb-4">
              Опрос Завершён! 🎉
            </h3>
            <p className="text-xl text-white/90 mb-8">
              Спасибо за твоё мнение! Твоя награда в 50 000 бьютисов готова к получению
            </p>
            <button
              onClick={() => {
                const element = document.getElementById('gift');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-white text-orange-600 px-10 py-4 rounded-full text-lg font-bold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Забрать Награду
            </button>
          </div>
        )}

        {!completed && (
          <div className="mt-8 flex justify-center gap-2">
            {questions.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index <= currentQuestion 
                    ? 'w-8 bg-gradient-to-r from-amber-500 to-orange-500' 
                    : 'w-2 bg-gray-300'
                }`}
              ></div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
